import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatCard, Icon } from '../components/Icon';
import { fmtDate } from '../lib/dates';
import { useModalEscape } from '../lib/useModalEscape';
import { ConversationModal, EmailPanel, UsagePanel, siteDomain, isAgency } from './Accounts';

/**
 * Agency section — an "agency" is an account that has a team (workspace members).
 * List agencies → open one → see its members → open a member to see THAT person's
 * own activity (their chats, sites, usage), segregated by who did what. Members
 * have no plan/account-type of their own (those belong to the agency), so no edit
 * form is shown for them, and they are NOT listed in the flat Accounts page —
 * instead they are findable here by email (a match surfaces their agency).
 */

interface Acct {
  id: string; email: string; name: string | null; company: string | null;
  plan_id: string | null; plan_name: string | null; status: string;
  credits_balance: number; credits_used_total: number; sites_count: number;
  account_type?: string | null; max_sites?: number; created_at?: string;
  member_count?: number; is_member?: boolean; member_of?: string[];
}
interface SiteUsage { site_id: string | null; site_url: string | null; credits_used: number; txns: number }
interface Member {
  principal: string; member_id: string | null; workspace_member_id: string | null;
  role: string; seat_type: string | null;
  account_id: string | null; email: string | null; name: string | null; status: string | null;
  last_login_at: string | null; created_at: string | null;
  credits_allocated: number | null; credits_used: number | null;
  conversation_count: number; sites_count: number | null;
}
interface Conv {
  id: string; title?: string; message_count?: number; last_message_at?: string;
  created_at?: string; first_message?: string; site_url?: string;
}
interface MSite { id?: string; site_url?: string; domain?: string; plugin_version?: string; wp_version?: string; last_seen_at?: string; is_active?: boolean }

const ROLE_BADGE: Record<string, string> = {
  owner: 'badge-purple', manager: 'badge-green', operator: 'badge-gray', wp_user: 'badge-amber',
};

export default function Agency() {
  const { can } = useAuth();
  const canWrite = can('accounts', 'write');
  const [accounts, setAccounts] = useState<Acct[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<{ agencyId: string; focus?: string } | null>(null);
  const [toDelete, setToDelete] = useState<Acct | null>(null);
  const [note, setNote] = useState('');

  async function load() {
    setLoading(true);
    try {
      const a = await backendApi.get<{ accounts: Acct[] }>('/license/admin/accounts');
      setAccounts(a.accounts || []); setError('');
    } catch (err: any) {
      setError((err as ApiError).isForbidden ? 'You do not have access to this section.' : err.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  const agencies = useMemo(() => accounts.filter(isAgency), [accounts]);
  const memberAccounts = useMemo(() => accounts.filter((a) => a.is_member), [accounts]);

  // A match on an agency's own fields OR on one of its member accounts (by email/
  // name) surfaces the agency — with the matching member(s) listed under it.
  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    const hit = (v?: string | null) => !!v && v.toLowerCase().includes(q);
    return agencies.map((agency) => {
      const direct = !q || hit(agency.email) || hit(agency.name) || hit(agency.company);
      const matchedMembers = q
        ? memberAccounts.filter((m) => (m.member_of || []).includes(agency.id) && (hit(m.email) || hit(m.name)))
        : [];
      return { agency, direct, matchedMembers };
    }).filter((r) => !search.trim() || r.direct || r.matchedMembers.length > 0);
  }, [agencies, memberAccounts, search]);

  const totalMembers = agencies.reduce((s, a) => s + (Number(a.member_count) || 0), 0);
  const totalSites = agencies.reduce((s, a) => s + (Number(a.sites_count) || 0), 0);
  const openAgency = open ? agencies.find((a) => a.id === open.agencyId) || null : null;

  return (
    <>
      <header className="page-head">
        <div><h1>Agencies</h1>
          <p className="muted">{agencies.length} agenc{agencies.length === 1 ? 'y' : 'ies'} — on the agency plan or with a team</p></div>
      </header>

      <div className="stat-row">
        <StatCard icon="users" tint="purple" value={agencies.length} label="Agencies" />
        <StatCard icon="users" tint="green" value={totalMembers} label="Members" />
        <StatCard icon="globe" tint="amber" value={totalSites} label="Sites" />
      </div>

      <div className="card">
        <div className="filter-bar">
          <input placeholder="Search by agency or member email, name…" value={search}
            onChange={(e) => setSearch(e.target.value)} style={{ flex: '1 1 240px' }} />
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {note && <div className="alert alert-ok">{note} <button className="alert-dismiss" onClick={() => setNote('')}>Dismiss</button></div>}

      <div className="card">
        <h2 className="card-title">All Agencies</h2>
        {loading ? <p className="muted">Loading…</p> : results.length === 0 ? (
          <p className="muted">{search.trim() ? 'No agency or member matches your search.' : 'No agencies yet — an account appears here once it is on the agency plan or has team members.'}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Agency</th><th>Plan</th><th>Members</th><th>Sites</th><th>Credits</th><th>Status</th>{canWrite && <th>Actions</th>}</tr></thead>
              <tbody>
                {results.map(({ agency: a, matchedMembers }) => [
                  <tr key={a.id} className="clickable-row" onClick={() => setOpen({ agencyId: a.id })}>
                    <td>{a.name || a.email}
                      {a.name && <span className="muted small block">{a.email}</span>}</td>
                    <td><span className="badge badge-purple">{a.plan_name || a.plan_id || 'free'}</span></td>
                    <td className="mono">{a.member_count || 0}</td>
                    <td className="mono">{a.sites_count || 0}</td>
                    <td className="mono">{(a.credits_balance || 0).toLocaleString()} left</td>
                    <td><span className={'badge ' + (a.status === 'active' ? 'badge-green' : 'badge-gray')}>{a.status}</span></td>
                    {canWrite && (
                      <td>
                        <div className="row-actions">
                          <button className="btn-icon danger" title="Delete agency" aria-label={`Delete agency ${a.email}`}
                            onClick={(e) => { e.stopPropagation(); setToDelete(a); }}><Icon name="trash" size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>,
                  ...matchedMembers.map((m) => (
                    <tr key={a.id + ':' + m.id} className="clickable-row submatch-row"
                      onClick={() => setOpen({ agencyId: a.id, focus: m.id })}>
                      <td colSpan={canWrite ? 7 : 6}>↳ member: <strong>{m.name || m.email}</strong>{m.name && m.email ? ` · ${m.email}` : ''}</td>
                    </tr>
                  )),
                ])}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toDelete && <DeleteAgencyModal agency={toDelete} onClose={() => setToDelete(null)}
        onDeleted={(msg) => { setToDelete(null); setOpen(null); setNote(msg); void load(); }} />}
      {openAgency && <AgencyModal agency={openAgency} focusAccountId={open?.focus} canWrite={canWrite} onChanged={load} onClose={() => setOpen(null)} />}
    </>
  );
}

function AgencyModal({ agency, focusAccountId, canWrite, onChanged, onClose }:
  { agency: Acct; focusAccountId?: string; canWrite: boolean; onChanged: () => void; onClose: () => void }) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [unattributed, setUnattributed] = useState(0);
  const [failed, setFailed] = useState(false);
  const [openMember, setOpenMember] = useState<Member | null>(null);
  const [atab, setAtab] = useState<'members' | 'sites' | 'chats' | 'usage' | 'plan'>('members');
  useModalEscape(onClose);

  useEffect(() => {
    backendApi.get<{ members: Member[]; unattributed_conversations: number }>(`/license/admin/accounts/${agency.id}/members`)
      .then((d) => {
        const list = d.members || [];
        setMembers(list); setUnattributed(d.unattributed_conversations || 0);
        if (focusAccountId) {
          const m = list.find((x) => x.account_id === focusAccountId);
          if (m) setOpenMember(m);
        }
      })
      .catch(() => setFailed(true));
  }, [agency.id, focusAccountId]);

  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal account-modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="account-modal-title">
            <h2 className="card-title" style={{ margin: 0 }}>{agency.name || agency.email}</h2>
            <span className="muted small">agency · {agency.plan_name || agency.plan_id} · {(agency.member_count || 0)} member{agency.member_count === 1 ? '' : 's'}</span>
          </div>
          <button className="btn-icon conv-modal-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="account-modal-body">
          <div className="seg" style={{ marginBottom: 14 }}>
            <button className={'seg-btn' + (atab === 'members' ? ' on' : '')} onClick={() => setAtab('members')}>Members ({agency.member_count || 0})</button>
            <button className={'seg-btn' + (atab === 'sites' ? ' on' : '')} onClick={() => setAtab('sites')}>Sites ({agency.sites_count || 0})</button>
            <button className={'seg-btn' + (atab === 'chats' ? ' on' : '')} onClick={() => setAtab('chats')}>Chats</button>
            <button className={'seg-btn' + (atab === 'usage' ? ' on' : '')} onClick={() => setAtab('usage')}>Usage</button>
            <button className={'seg-btn' + (atab === 'plan' ? ' on' : '')} onClick={() => setAtab('plan')}>Plan</button>
          </div>

          {atab === 'members' && (
            failed ? <p className="muted small">Failed to load members.</p>
            : !members ? <p className="muted small">Loading…</p> : (
              <>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Member</th><th>Role</th><th>Seat</th><th>Chats</th><th>Sites</th><th>Credits</th></tr></thead>
                    <tbody>
                      {members.filter((m) => m.role !== 'wp_user').map((m) => (
                        <tr key={m.principal} className="clickable-row" onClick={() => setOpenMember(m)}>
                          <td>{m.name || m.email || m.principal}
                            {m.email && m.name && <span className="muted small block">{m.email}</span>}</td>
                          <td><span className={'badge ' + (ROLE_BADGE[m.role] || 'badge-gray')}>{m.role}</span></td>
                          <td className="muted">{m.seat_type || '—'}</td>
                          <td className="mono">{m.conversation_count}</td>
                          <td className="mono">{m.sites_count == null ? '—' : m.sites_count}</td>
                          <td className="mono">{m.credits_allocated == null ? '—' : `${(m.credits_used || 0).toLocaleString()} / ${(m.credits_allocated).toLocaleString()}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {members && !members.some((m) => m.role === 'manager' || m.role === 'operator') &&
                  <p className="muted small" style={{ marginTop: 10 }}>No team members invited yet — this account is on the agency plan.</p>}

                {/* Site chat users — people who chatted on the sites via WordPress
                    (incl. the owner's own site logins). Not team members, but they
                    hold the chats, so list them clickable so "who did what" resolves. */}
                {(members.some((m) => m.role === 'wp_user') || unattributed > 0) && (
                  <>
                    <div className="ad-section-title">Site chat users</div>
                    <p className="muted small" style={{ margin: '0 0 8px' }}>Chats made on the sites via WordPress logins (not team members).</p>
                    <div className="table-wrap">
                      <table className="table">
                        <thead><tr><th>Site user</th><th>Chats</th></tr></thead>
                        <tbody>
                          {members.filter((m) => m.role === 'wp_user').map((m) => (
                            <tr key={m.principal} className="clickable-row" onClick={() => setOpenMember(m)}>
                              <td>{m.name || m.principal}</td>
                              <td className="mono">{m.conversation_count}</td>
                            </tr>
                          ))}
                          {unattributed > 0 && (
                            <tr className="clickable-row" onClick={() => setOpenMember({
                              principal: '__null__', member_id: null, workspace_member_id: null, role: 'wp_user', seat_type: null,
                              account_id: null, email: null, name: 'Unattributed / shared', status: null,
                              last_login_at: null, created_at: null, credits_allocated: null, credits_used: null,
                              conversation_count: unattributed, sites_count: null,
                            })}>
                              <td>Unattributed / shared</td>
                              <td className="mono">{unattributed}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )
          )}

          {atab === 'sites' && <AgencySites agencyId={agency.id} />}
          {atab === 'chats' && <MemberChats agencyId={agency.id} />}
          {atab === 'usage' && <UsagePanel accountId={agency.id} />}
          {atab === 'plan' && <AgencyPlan agency={agency} canWrite={canWrite} onChanged={onChanged} />}
        </div>
      </div>
      {openMember && <MemberModal agency={agency} member={openMember} onClose={() => setOpenMember(null)} />}
    </div>, document.body);
}

type Tab = 'chats' | 'sites' | 'usage' | 'email';

function MemberModal({ agency, member, onClose }: { agency: Acct; member: Member; onClose: () => void }) {
  const isOwner = member.role === 'owner';
  const isWp = member.role === 'wp_user';
  const canEmail = !!member.email;
  const [tab, setTab] = useState<Tab>('chats');
  useModalEscape(onClose);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'chats', label: `Chats (${member.conversation_count})` },
    { key: 'sites', label: 'Sites' },
    { key: 'usage', label: 'Usage' },
    ...(canEmail ? [{ key: 'email' as Tab, label: 'Email' }] : []),
  ];

  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal account-modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="account-modal-title">
            <h2 className="card-title" style={{ margin: 0 }}>{member.name || member.email || member.principal}</h2>
            <span className="muted small">
              <span className={'badge ' + (ROLE_BADGE[member.role] || 'badge-gray')}>{member.role}</span>
              {' '}under {agency.name || agency.email}
            </span>
          </div>
          <button className="btn-icon conv-modal-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="account-modal-body">
          {/* Identity — no plan / account-type (those belong to the agency) */}
          <dl className="detail-grid">
            {member.email && <><dt>Email</dt><dd>{member.email}</dd></>}
            <dt>Role</dt><dd>{member.role}{member.seat_type ? ` · ${member.seat_type}` : ''}</dd>
            {member.status && <><dt>Status</dt><dd>{member.status}</dd></>}
            {member.created_at && <><dt>Joined</dt><dd>{fmtDate(member.created_at)}</dd></>}
            {member.last_login_at && <><dt>Last login</dt><dd>{fmtDate(member.last_login_at)}</dd></>}
          </dl>

          <div className="seg" style={{ margin: '14px 0' }}>
            {tabs.map((t) => (
              <button key={t.key} className={'seg-btn' + (tab === t.key ? ' on' : '')} onClick={() => setTab(t.key)}>{t.label}</button>
            ))}
          </div>

          {tab === 'chats' && <MemberChats agencyId={agency.id} principal={member.principal} />}
          {tab === 'sites' && <MemberSites agencyId={agency.id} member={member} isOwner={isOwner} isWp={isWp} />}
          {tab === 'usage' && <MemberUsage agency={agency} member={member} isOwner={isOwner} />}
          {tab === 'email' && canEmail && <EmailPanel email={member.email!} onSent={() => { /* stays open */ }} />}
        </div>
      </div>
    </div>, document.body);
}

function MemberChats({ agencyId, principal }: { agencyId: string; principal?: string }) {
  const [convos, setConvos] = useState<Conv[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  useEffect(() => {
    const url = `/license/admin/accounts/${agencyId}/conversations${principal ? `?principal=${encodeURIComponent(principal)}` : ''}`;
    backendApi.get<{ conversations: Conv[] }>(url)
      .then((d) => setConvos(d.conversations || [])).catch(() => setConvos([]));
  }, [agencyId, principal]);

  if (!convos) return <p className="muted small">Loading…</p>;
  if (convos.length === 0) return <p className="muted small">{principal ? 'No chats by this member.' : 'No chats on this agency.'}</p>;

  // Group by site so multiple sites read as sections, not one mixed pile.
  const groups = new Map<string, Conv[]>();
  convos.forEach((c) => { const k = c.site_url || ''; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(c); });
  const keys = [...groups.keys()].sort((a, b) => (a ? a : '~').localeCompare(b ? b : '~'));

  return (
    <>
      {keys.map((k) => (
        <div key={k || 'none'} style={{ marginBottom: 16 }}>
          <div className="ad-section-title" style={{ marginTop: 0 }}>
            {k ? siteDomain(k) : 'No site'} · {groups.get(k)!.length} chat{groups.get(k)!.length === 1 ? '' : 's'}
          </div>
          <div className="conv-grid">
            {groups.get(k)!.map((c) => (
              <button key={c.id} type="button" className="conv-card" onClick={() => setOpenId(c.id)}>
                <div className="conv-card-head">
                  <span className="conv-card-title">{c.title || 'Untitled'}</span>
                  <span className="conv-card-time">{fmtDate(c.last_message_at || c.created_at)}</span>
                </div>
                <div className="conv-card-preview">{c.first_message ? c.first_message.slice(0, 90) : 'No messages'}</div>
                <div className="conv-card-foot">
                  <span className="conv-card-msgs">{c.message_count ?? 0} msgs</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
      {openId && <ConversationModal conversationId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function MemberSites({ agencyId, member, isOwner, isWp }:
  { agencyId: string; member: Member; isOwner: boolean; isWp: boolean }) {
  const [rows, setRows] = useState<MSite[] | null>(null);

  useEffect(() => {
    if (isWp) { setRows([]); return; }
    if (isOwner) {
      // Owner has full access to the agency's sites — fetch them by account_id
      // (not a full /admin/sites download filtered by email).
      backendApi.get<{ sites?: MSite[] }>(`/license/admin/accounts/${agencyId}`)
        .then((d) => setRows(d.sites || [])).catch(() => setRows([]));
    } else if (member.workspace_member_id) {
      backendApi.get<{ sites: MSite[] }>(`/license/admin/accounts/${agencyId}/members/${member.workspace_member_id}/sites`)
        .then((d) => setRows(d.sites || [])).catch(() => setRows([]));
    } else { setRows([]); }
  }, [agencyId, member.workspace_member_id, isOwner, isWp]);

  if (isWp) return <p className="muted small">Site access isn't tracked for un-mapped WordPress users.</p>;
  if (rows === null) return <p className="muted small">Loading…</p>;
  return (
    <>
      {isOwner && <p className="muted small" style={{ marginTop: 0 }}>Owner — full access to all agency sites.</p>}
      <SiteTable rows={rows} emptyLabel={`No sites ${isOwner ? 'on this agency' : 'assigned to this member'}.`} />
    </>
  );
}

/** Agency-level sites tab — all sites owned by the agency account. */
function AgencySites({ agencyId }: { agencyId: string }) {
  const [rows, setRows] = useState<MSite[] | null>(null);
  useEffect(() => {
    backendApi.get<{ sites?: MSite[] }>(`/license/admin/accounts/${agencyId}`)
      .then((d) => setRows(d.sites || [])).catch(() => setRows([]));
  }, [agencyId]);
  if (rows === null) return <p className="muted small">Loading…</p>;
  return <SiteTable rows={rows} emptyLabel="No sites on this agency." />;
}

/** Shared sites table (linked domain + plugin/WP/last-seen/status). */
function SiteTable({ rows, emptyLabel }: { rows: MSite[]; emptyLabel: string }) {
  if (rows.length === 0) return <p className="muted small">{emptyLabel}</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>Site</th><th>Plugin</th><th>WP</th><th>Last Seen</th><th>Status</th></tr></thead>
        <tbody>{rows.map((s, i) => {
          const url = s.site_url || s.domain || '';
          return (
            <tr key={s.id || i}>
              <td className="mono small">{url
                ? <a className="link" href={/^https?:\/\//.test(url) ? url : `https://${url}`} target="_blank" rel="noreferrer">{siteDomain(url)}</a>
                : (s.id || '—')}</td>
              <td className="mono">{s.plugin_version || '—'}</td>
              <td className="mono">{s.wp_version || '—'}</td>
              <td className="muted">{fmtDate(s.last_seen_at)}</td>
              <td><span className={'badge ' + (s.is_active !== false ? 'badge-green' : 'badge-gray')}>{s.is_active !== false ? 'active' : 'inactive'}</span></td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function MemberUsage({ agency, member, isOwner }: { agency: Acct; member: Member; isOwner: boolean }) {
  const [bySite, setBySite] = useState<SiteUsage[] | null>(null);
  const wmId = member.workspace_member_id;
  useEffect(() => {
    if (!wmId) { setBySite(null); return; }
    backendApi.get<{ bySite: SiteUsage[] }>(`/license/admin/accounts/${agency.id}/members/${wmId}/usage`)
      .then((d) => setBySite(d.bySite || [])).catch(() => setBySite([]));
  }, [agency.id, wmId]);

  // Owner's pool is the agency account's own balance (no per-seat allocation).
  if (isOwner) {
    return (
      <div className="uc-summary">
        <div className="uc-stat"><span className="uc-stat-val" style={{ color: 'var(--color-status-success-accent)' }}>{(agency.credits_balance || 0).toLocaleString()}</span><span className="uc-stat-lbl">Agency credits left</span></div>
        <div className="uc-stat"><span className="uc-stat-val" style={{ color: '#EF4444' }}>{(agency.credits_used_total || 0).toLocaleString()}</span><span className="uc-stat-lbl">Agency credits used</span></div>
        <div className="uc-stat"><span className="uc-stat-val">{member.conversation_count}</span><span className="uc-stat-lbl">Chats</span></div>
      </div>
    );
  }
  const allocated = member.credits_allocated;
  return (
    <>
      <div className="uc-summary">
        <div className="uc-stat"><span className="uc-stat-val">{allocated == null ? '∞' : allocated.toLocaleString()}</span><span className="uc-stat-lbl">Credits allocated</span></div>
        <div className="uc-stat"><span className="uc-stat-val" style={{ color: '#EF4444' }}>{(member.credits_used || 0).toLocaleString()}</span><span className="uc-stat-lbl">Credits used</span></div>
        <div className="uc-stat"><span className="uc-stat-val">{member.conversation_count}</span><span className="uc-stat-lbl">Chats</span></div>
      </div>

      <div className="ad-section-title">Credits used per site</div>
      {bySite === null ? <p className="muted small">{wmId ? 'Loading…' : 'No per-site breakdown for this member.'}</p>
        : bySite.length === 0 ? <p className="muted small">No credit usage recorded for this member.</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Site</th><th>Credits Used</th></tr></thead>
              <tbody>{bySite.map((r, i) => (
                <tr key={r.site_id || i}>
                  <td className="mono small">{r.site_url ? siteDomain(r.site_url) : (r.site_id ? r.site_id : '— no site —')}</td>
                  <td className="mono">{Number(r.credits_used).toLocaleString()}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
    </>
  );
}

/** Compact plan / credits summary for the agency account, plus admin actions. */
function AgencyPlan({ agency, canWrite, onChanged }: { agency: Acct; canWrite: boolean; onChanged: () => void }) {
  const [status, setStatus] = useState(agency.status);
  const [credits, setCredits] = useState('');
  const [creditsRemove, setCreditsRemove] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');

  async function saveStatus() {
    if (status === agency.status) return;
    const cascade = (status === 'suspended' || status === 'cancelled') && (agency.member_count || 0) > 0;
    if (cascade && !confirm(`Set ${agency.email} to "${status}"?\n\nThis also sets its ${agency.member_count} member account(s) to "${status}".`)) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await backendApi.put(`/license/admin/accounts/${agency.id}`, { status });
      setMsg(`Status set to ${status}.`); onChanged();
    } catch (e: any) { setErr(e.body?.error || e.message); } finally { setBusy(false); }
  }
  async function addCredits() {
    const n = Number(credits);
    if (!n) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await backendApi.put(`/license/admin/accounts/${agency.id}`, { credits: n });
      setMsg(`Added ${n.toLocaleString()} credits.`); setCredits(''); onChanged();
    } catch (e: any) { setErr(e.body?.error || e.message); } finally { setBusy(false); }
  }
  async function removeCredits() {
    const n = Number(creditsRemove);
    if (!n) return;
    const balance = agency.credits_balance || 0;
    if (n > balance) { setErr(`Can't remove more than the ${balance.toLocaleString()} credits this agency has.`); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      await backendApi.put(`/license/admin/accounts/${agency.id}`, { creditsRemove: n });
      setMsg(`Removed ${n.toLocaleString()} credits.`); setCreditsRemove(''); onChanged();
    } catch (e: any) { setErr(e.body?.error || e.message); } finally { setBusy(false); }
  }

  return (
    <>
      <div className="uc-summary">
        <div className="uc-stat"><span className="uc-stat-val" style={{ color: 'var(--color-status-success-accent)' }}>{(agency.credits_balance || 0).toLocaleString()}</span><span className="uc-stat-lbl">Credits left</span></div>
        <div className="uc-stat"><span className="uc-stat-val" style={{ color: '#EF4444' }}>{(agency.credits_used_total || 0).toLocaleString()}</span><span className="uc-stat-lbl">Credits used</span></div>
        <div className="uc-stat"><span className="uc-stat-val">{agency.sites_count || 0}{agency.max_sites ? ` / ${agency.max_sites}` : ''}</span><span className="uc-stat-lbl">Sites</span></div>
        <div className="uc-stat"><span className="uc-stat-val">{agency.member_count || 0}</span><span className="uc-stat-lbl">Members</span></div>
      </div>
      <div className="ad-section-title">Plan</div>
      <dl className="detail-grid">
        <dt>Plan</dt><dd>{agency.plan_name || agency.plan_id || '—'}</dd>
        <dt>Account type</dt><dd>{agency.account_type || 'customer'}</dd>
        <dt>Status</dt><dd>{agency.status}</dd>
        <dt>Email</dt><dd>{agency.email}</dd>
        {agency.created_at && <><dt>Joined</dt><dd>{fmtDate(agency.created_at)}</dd></>}
      </dl>

      {canWrite && (
        <>
          <div className="ad-section-title">Actions</div>
          {err && <div className="alert alert-error">{err}</div>}
          {msg && <div className="alert alert-ok">{msg}</div>}
          <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ marginBottom: 0 }}><span>Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {['active', 'suspended', 'cancelled'].map((s) => <option key={s}>{s}</option>)}
              </select></label>
            <button className="btn btn-sm" disabled={busy || status === agency.status} onClick={saveStatus}>Save status</button>
            <label className="field" style={{ marginBottom: 0 }}><span>Add credits</span>
              <input type="number" min={0} placeholder="e.g. 500" value={credits} onChange={(e) => setCredits(e.target.value)} /></label>
            <button className="btn btn-sm btn-primary" disabled={busy || !Number(credits)} onClick={addCredits}>Add</button>
            <label className="field" style={{ marginBottom: 0 }}><span>Remove credits</span>
              <input type="number" min={0} max={agency.credits_balance || 0} placeholder={`max ${(agency.credits_balance || 0).toLocaleString()}`} value={creditsRemove} onChange={(e) => setCreditsRemove(e.target.value)} /></label>
            <button className="btn btn-sm" disabled={busy || !Number(creditsRemove) || Number(creditsRemove) > (agency.credits_balance || 0)} onClick={removeCredits}>Remove</button>
          </div>
          {(agency.member_count || 0) > 0 &&
            <p className="muted small" style={{ marginTop: 8 }}>Suspending or cancelling also applies to this agency's {agency.member_count} member account(s).</p>}
        </>
      )}
    </>
  );
}

interface DeletePlan {
  agency: { id: string; email: string; name: string | null };
  hasSubscription: boolean; sites: number; inactiveSites?: number; sandboxes: number; pendingInvites: number;
  membersToDelete: { id: string; email: string; name: string | null; role: string }[];
  membersToKeep: { id: string; email: string; name: string | null; role: string; reasonText: string }[];
}
interface DeleteResult {
  agencyDeleted?: boolean; agencyError?: string | null;
  deletedMembers: { id: string; email: string }[];
  keptMembers: { id: string; email: string }[];
  failedMembers: { id: string; email: string; error: string }[];
  billing: { cancelledSubs: string[]; voidedInvoices: string[]; warnings: string[] };
}

/**
 * Deleting an agency: shows exactly what goes (the agency, the member accounts
 * that belong to it alone, sites and sandboxes, billing) and which members are
 * kept and why, then asks for the agency's email typed back before deleting.
 */
export function DeleteAgencyModal({ agency, onClose, onDeleted }:
  { agency: { id: string; email: string; name?: string | null }; onClose: () => void; onDeleted: (message: string) => void }) {
  const [plan, setPlan] = useState<DeletePlan | null>(null);
  const [error, setError] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DeleteResult | null>(null);
  useModalEscape(() => { if (!busy) onClose(); });

  useEffect(() => {
    let live = true;
    backendApi.get<{ plan: DeletePlan }>(`/license/admin/accounts/${encodeURIComponent(agency.id)}/delete-plan`)
      .then((d) => { if (live) setPlan(d.plan); })
      .catch((err: any) => { if (live) setError(err.status === 404 ? 'This agency no longer exists.' : (err.body?.error || err.message)); });
    return () => { live = false; };
  }, [agency.id]);

  async function remove() {
    if (!plan) return;
    setBusy(true); setError('');
    // The member ids shown here: the server refuses if its fresh plan differs.
    const expected = plan.membersToDelete.map((m) => encodeURIComponent(m.id)).join(',');
    try {
      const r = await backendApi.delete<DeleteResult>(
        `/license/admin/accounts/${encodeURIComponent(agency.id)}?withMembers=true&confirm=true&expectedMembers=${expected}`);
      setResult(r);
    } catch (err: any) {
      const body = err.body || {};
      if (body.code === 'plan_changed' && body.plan) {
        // Show the fresh plan and make the admin confirm it again.
        setPlan(body.plan); setTyped('');
        setError('This agency changed since you opened this. Review the updated list below and confirm again.');
      } else if (body.code === 'billing_failed') {
        // No account was deleted, but billing that was stopped before the
        // failure stays stopped: say so, or the admin assumes nothing changed.
        const cancelled: string[] = body.billing?.cancelledSubs || [];
        setError('Nothing was deleted: stopping Stripe billing failed'
          + (body.billing?.warnings?.length ? ` (${body.billing.warnings.join('; ')})` : '') + '.'
          + (cancelled.length
            ? ` Already cancelled before the failure: ${cancelled.join(', ')}. Those accounts still exist but are no longer billed; delete again to finish.`
            : ' No billing was changed.')
          + ' Try again in a few minutes.');
      } else if (body.code === 'partial') {
        // Members were deleted but the agency account was not: show what happened.
        setResult(body as DeleteResult);
      } else if (!body.code && (err.status >= 500 || !err.status)) {
        // No answer from our server (timeout, gateway error, network): part of
        // the delete may have run. Don't let the admin assume either way.
        setError(`The server did not answer cleanly (${err.message}). Some of the delete may have happened: close this, reload Agencies to check, then try again if the agency is still listed.`);
      } else {
        setError(body.error || err.message);
      }
    }
    finally { setBusy(false); }
  }

  const n = plan?.membersToDelete.length ?? 0;
  const confirmed = typed.trim().toLowerCase() === agency.email.toLowerCase();

  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); if (!busy) onClose(); }}>
      <div className="modal modal-wide" role="alertdialog" aria-modal="true" aria-label="Delete agency" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h2 className="card-title" style={{ margin: 0 }}>Delete agency {agency.name || agency.email}?</h2>
          <button className="btn-icon conv-modal-close" aria-label="Close" disabled={busy} onClick={onClose}>×</button></div>
        {error && <div className="alert alert-error">{error}</div>}
        {result ? (
          <>
            {result.agencyDeleted === false
              ? <div className="alert alert-error">The agency account {agency.email} could not be deleted ({result.agencyError}). Deleted {result.deletedMembers.length} member account{result.deletedMembers.length === 1 ? '' : 's'}; try deleting the agency again.</div>
              : <div className="alert alert-ok">Deleted {agency.email} and {result.deletedMembers.length} member account{result.deletedMembers.length === 1 ? '' : 's'}.</div>}
            {result.failedMembers.length > 0 && <div className="alert alert-error">Could not delete: {result.failedMembers.map((m) => `${m.email} (${m.error})`).join(', ')}</div>}
            {result.billing.warnings.length > 0 && <div className="alert alert-error">Billing needs a manual check in Stripe: {result.billing.warnings.join('; ')}</div>}
            {result.billing.cancelledSubs.length > 0 && <p className="muted small">Cancelled subscriptions: {result.billing.cancelledSubs.join(', ')}</p>}
            {result.keptMembers.length > 0 && <p className="muted small">Kept member accounts: {result.keptMembers.map((m) => m.email).join(', ')}</p>}
            <div className="row-actions"><button className="btn btn-sm" onClick={() => onDeleted(result.agencyDeleted === false
              ? `${agency.email} was NOT deleted (${result.deletedMembers.length} member account${result.deletedMembers.length === 1 ? '' : 's'} were). Try again.`
              : `${agency.email} deleted with ${result.deletedMembers.length} member account${result.deletedMembers.length === 1 ? '' : 's'}.`
              + (result.billing.warnings.length ? ' Check Stripe: stopping billing reported a problem.' : ''))}>Done</button></div>
          </>
        ) : !plan ? (!error && <p className="muted small">Loading what this would delete…</p>) : (
          <>
            <p style={{ margin: '12px 0 6px' }}>This permanently deletes:</p>
            <ul className="small" style={{ margin: '0 0 10px 18px' }}>
              <li>the agency account <strong>{plan.agency.email}</strong>, its {plan.sites} active site{plan.sites === 1 ? '' : 's'}
                {plan.sandboxes ? ` (${plan.sandboxes} live sandbox${plan.sandboxes === 1 ? '' : 'es'} torn down)` : ''}
                {plan.inactiveSites ? `, ${plan.inactiveSites} old site record${plan.inactiveSites === 1 ? '' : 's'} (already disconnected or destroyed)` : ''}, chats, credits and history;</li>
              <li>{n === 0 ? 'no member accounts' : <>{n} member account{n === 1 ? '' : 's'}: {plan.membersToDelete.map((m) => m.email).join(', ')}</>};</li>
              <li>the team: memberships{plan.pendingInvites ? `, ${plan.pendingInvites} pending invite${plan.pendingInvites === 1 ? '' : 's'}` : ''}, site assignments and member keys;</li>
              <li>{plan.hasSubscription ? 'its Stripe subscription is cancelled (and open invoices voided)' : 'any WPOS billing on these accounts is stopped'}.</li>
            </ul>
            {plan.membersToKeep.length > 0 && (
              <div className="alert" style={{ marginBottom: 10 }}>
                <strong>Kept</strong> (removed from this agency only):
                <ul className="small" style={{ margin: '4px 0 0 18px' }}>
                  {plan.membersToKeep.map((m) => <li key={m.id}>{m.email}: {m.reasonText}</li>)}
                </ul>
              </div>
            )}
            <label className="field"><span>Type <strong>{agency.email}</strong> to confirm</span>
              <input aria-label="Type the agency email to confirm" value={typed} onChange={(e) => setTyped(e.target.value)} disabled={busy} /></label>
            <div className="row-actions" style={{ marginTop: 12 }}>
              <button className="btn btn-danger btn-sm" disabled={!confirmed || busy} onClick={() => void remove()}>
                {busy ? 'Deleting…' : `Delete agency${n ? ` and ${n} member account${n === 1 ? '' : 's'}` : ''}`}</button>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>, document.body);
}
